import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DeleteBySelectionModalComponent } from '../delete-by-selection-modal/delete-by-selection-modal.component';
import { FormControl, FormGroup } from '@angular/forms';
import { MapperPlaceResult } from '../../class/location';
import PlaceResult = google.maps.places.PlaceResult;
import { StorageService } from '../../services';
import { ToastService } from '../../services/toast/toast.service';
import { Phone } from '../../interfaces';
import { FormatEmailValidatorAllowNull } from '../../validators/email';
import { forkJoin, Subscription } from 'rxjs';
import { IBillingAddress, IBudgetsSetting } from '../../../../types-legacy';
import { BudgetService } from '../../services/budgets';

@Component({
  selector: 'roma-update-company-data',
  templateUrl: './update-company-data.component.html',
  styleUrls: ['./update-company-data.component.scss'],
})
export class UpdateCompanyDataComponent implements OnInit, OnDestroy {
  isLoading = false;
  showToolTip = false;
  form = new FormGroup({
    company_name: new FormControl(null),
    tradename: new FormControl(null),
    email: new FormControl(null, FormatEmailValidatorAllowNull()),
    fiscalName: new FormControl(null),
    owner_id: new FormControl(StorageService.CompanyId),
    phone: new FormControl(null),
    address: new FormControl(null),
    business_type: new FormControl(null),
    fiscal_regime: new FormControl(null),
  });
  isResponsive = window.innerWidth <= 768;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.isResponsive = window.innerWidth <= 768;
  }

  budget: IBudgetsSetting;
  private sub$ = new Subscription();

  constructor(
    private readonly dialogRef: MatDialogRef<UpdateCompanyDataComponent>,
    private readonly dialog: MatDialog,
    private readonly i18n: TranslateService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly toastService: ToastService,
    private readonly budgetService: BudgetService
  ) {}

  async ngOnInit(): Promise<void> {
    this.getSettings();
    this.dialogRef.disableClose = true;
    this.dialogRef
      .backdropClick()
      .subscribe(async () => await this.onBackdropClick());
  }

  ngOnDestroy(): void {
    this.sub$.unsubscribe();
  }

  close(): void {
    this.onBackdropClick().finally(() => (this.isLoading = false));
  }

  private draw() {
    this.cdRef.detectChanges();
  }

  validateForm(): void {
    this.form.markAllAsTouched();
    this.draw();
  }

  parseAddress(addressObj: IBillingAddress): string {
    const { address, city, country, postalcode, state } = addressObj || {};
    return [address, city, state, postalcode, country]
      .filter(Boolean)
      .join(', ');
  }

  parseForm(): void {
    this.form.patchValue({
      company_name: this.budget?.data_account?.company_name,
      tradename: this.budget?.data_account?.tradename,
      email: this.budget?.data_account?.email,
      fiscalName: this.budget?.data_account?.fiscalName,
      phone: this.budget?.data_account?.phone,
      address: this.parseAddress(this.budget?.billing_addres),
    });
  }

  getSettings(): void {
    const id = StorageService.CompanyId;

    this.sub$.add(
      forkJoin([
        this.budgetService.findSettingsByCompany(id),
        this.budgetService.findAllPaymentMethodsByCompany_SELECT(),
      ]).subscribe(
        (result) => {
          const settings = result[0] as IBudgetsSetting;

          if (settings) {
            this.budget = settings;
            this.parseForm();
          } else {
            this.budget.idOwner = id;
            this.createSettings();
          }
        },
        (error) => {
          if (error.statusCode === 404) {
            this.budget.idOwner = id;
            this.createSettings();
          }
        }
      )
    );
  }

  createSettings(): void {
    this.budgetService.createOneSetting(this.budget).subscribe((data) => {
      this.budget = data;
      this.parseForm();
    });
  }

  async submit(): Promise<void> {
    this.isLoading = true;
    this.validateForm();
    this.setObject();
    try {
      await this.budgetService
        .updateOneSettings(this.budget._id, this.budget)
        .toPromise();
      this.isLoading = false;
      this.dialogRef.close(true);
    } catch (e) {
      this.isLoading = false;
      this.toastService.show({
        text: 'backendErrors.genericError',
        type: 'error',
        msDuration: 4000,
      });
    }
  }

  setObject(): void {
    const values = this.form.value;
    this.budget.data_account.company_name = values.company_name;
    this.budget.data_account.tradename = values.tradename;
    this.budget.data_account.email = values.email;
    this.budget.data_account.phone = values.phone;
    this.budget.data_account.fiscalName = values.fiscalName;
    this.budget.idOwner = values.owner_id;
    this.budget.data_account.business_type = values.business_type;
  }

  autocompleteSelected(result: PlaceResult): void {
    const location = MapperPlaceResult(result);
    this.budget.billing_addres.address = location.name;
    this.budget.billing_addres.city = location.city;
    this.budget.billing_addres.postalcode = location.postCode;
    this.budget.billing_addres.state = location.state;
    this.budget.billing_addres.country = location.codeCountry;
    this.draw();
  }

  phoneChanges(event: Phone): void {
    const phones = this.form.value['phonesArr'];
    phones[0] = event;
    this.form.patchValue({
      phonesArr: phones,
    });
  }

  displayTooltip(canDisplay = true): void {
    if (this.isResponsive || !this.form.invalid) return;
    this.showToolTip = canDisplay;
  }

  changeName(event: InputEvent): void {
    const inputElement = event.target as HTMLInputElement;
    this.form.patchValue({
      name: inputElement.value,
    });
    this.validateForm();
    this.draw();
  }

  public async onBackdropClick(): Promise<void> {
    if (this.form.touched) {
      const dialogRef = this.dialog.open(DeleteBySelectionModalComponent, {
        panelClass: 'delete-by-selection-modal',
        data: {
          title: this.i18n.instant('general.withoutSave'),
          confirmLabel: this.i18n.instant('general.buttonExit'),
          showBody: false,
        },
      });

      dialogRef.afterClosed().subscribe((res) => {
        if (res == 'EXECUTE') {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }
}
